import { APP_ENVIRONMENT } from "../../config/config";
import { Vector2 } from "./types";
import { showError } from "./utils";

export function FrameBufferCheck(gl: WebGL2RenderingContext, passname = "") {
    if (APP_ENVIRONMENT === "development") {
        const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
        if (status !== gl.FRAMEBUFFER_COMPLETE) {
            showError(passname + " Framebuffer is incomplete");
        }
    }
}

export const CubemapFaceIndexMap: { [index: number]: number } = {
    0: WebGL2RenderingContext.TEXTURE_CUBE_MAP_POSITIVE_X,
    1: WebGL2RenderingContext.TEXTURE_CUBE_MAP_NEGATIVE_X,
    2: WebGL2RenderingContext.TEXTURE_CUBE_MAP_POSITIVE_Y,
    3: WebGL2RenderingContext.TEXTURE_CUBE_MAP_NEGATIVE_Y,
    4: WebGL2RenderingContext.TEXTURE_CUBE_MAP_POSITIVE_Z,
    5: WebGL2RenderingContext.TEXTURE_CUBE_MAP_NEGATIVE_Z,
};

export function CreateTextureRT(
    gl: WebGL2RenderingContext,
    size: Vector2,
    internalFormat: GLenum,
    format: GLenum,
    type: GLenum,
    bGenerateMips = false,
) {
    const rtTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, rtTexture);
    {
        // define size and format of level 0
        const level = 0;
        const border = 0;
        const data = null;
        gl.texImage2D(gl.TEXTURE_2D, level, internalFormat, size.x, size.y, border, format, type, data);

        if (bGenerateMips) {
            gl.generateMipmap(gl.TEXTURE_2D);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST_MIPMAP_LINEAR);
        } else {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        }
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    }
    return rtTexture;
}

export function CreateCubemapRT(
    gl: WebGL2RenderingContext,
    size: number,
    internalFormat: GLenum,
    format: GLenum,
    type: GLenum,
    bGenerateMips = false,
) {
    const cubemap = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, cubemap);

    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);

    for (let i = 0; i < 6; i++) {
        const targetFace = CubemapFaceIndexMap[i];
        const level = 0;
        gl.texImage2D(targetFace, level, internalFormat, size, size, 0, format, type, null);
    }

    if (bGenerateMips) {
        gl.generateMipmap(gl.TEXTURE_CUBE_MAP);
        gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    } else {
        gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    }

    return cubemap;
}

export function CreateCubemapFramebuffers(
    gl: WebGL2RenderingContext,
    cubemapTexture: WebGLTexture,
): WebGLFramebuffer[] {
    const fbos: WebGLFramebuffer[] = [];
    for (let i = 0; i < 6; i++) {
        const targetFace = CubemapFaceIndexMap[i];
        //Framebuffer
        fbos[i] = gl.createFramebuffer()!;
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbos[i]);
        //Attach face
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, targetFace, cubemapTexture, 0);
        FrameBufferCheck(gl, "CubemapFrameBuffer");
    }
    return fbos;
}

export function CreateBuffer(
    gl: WebGL2RenderingContext,
    srcDataOrSize: AllowSharedBufferSource | number,
    usage: number,
): WebGLBuffer {
    const buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);

    if (typeof srcDataOrSize === "number") {
        // If srcDataOrSize is a number, allocate a buffer of that size
        gl.bufferData(gl.ARRAY_BUFFER, srcDataOrSize, usage);
    } else {
        // If srcDataOrSize is a buffer source, use it directly
        gl.bufferData(gl.ARRAY_BUFFER, srcDataOrSize, usage);
    }
    return buf;
}

export function DefineVertexAttrib(
    gl: WebGL2RenderingContext,
    index: number,
    buffer: WebGLBuffer,
    numElements: number,
    type: number,
    strideSizeBytes: number,
) {
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(index);
    gl.vertexAttribPointer(index, numElements, type, false, strideSizeBytes, 0);
}

export function BindRenderTarget(
    gl: WebGL2RenderingContext,
    fbo: WebGLFramebuffer,
    viewportSize: Vector2,
    bClear = false,
    bClearDepth = false,
    bClearStencil = false,
) {
    gl.viewport(0.0, 0.0, viewportSize.x, viewportSize.y);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    if (bClear) {
        gl.clearColor(0.0, 0.0, 0.0, 0.0);
        if (bClearDepth) {
            gl.clearDepth(1.0); // Set clear depth to maximum
            if (bClearStencil) {
                gl.stencilMask(0xff);
                gl.clearStencil(0x0);
                gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
            } else {
                gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
            }
        } else {
            gl.clear(gl.COLOR_BUFFER_BIT);
        }
    }
}

export function CreateFramebufferWithAttachment(gl: WebGL2RenderingContext, texture: WebGLTexture) {
    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    FrameBufferCheck(gl, "FrameBuffer");
    return fbo;
}

//Async Read
function CPUWaitForGPUAsync(gl: WebGL2RenderingContext, sync: WebGLSync, flags: number, interval_ms: number) {
    return new Promise<void>((resolve, reject) => {
        function syncCheck() {
            const res = gl.clientWaitSync(sync, flags, 0);
            if (res === gl.WAIT_FAILED) {
                //Indicates that an error occurred during the execution.
                reject();
                return;
            }
            if (res === gl.TIMEOUT_EXPIRED) {
                //Indicates that the timeout time passed and that the sync object did not become signaled.
                setTimeout(syncCheck, interval_ms); //try again after interval_ms passed
                return;
            }
            resolve();
        }
        syncCheck();
    });
}
async function GetBufferSubDataAsync(
    gl: WebGL2RenderingContext,
    target: number,
    buffer: WebGLBuffer,
    srcByteOffset: number,
    dstBuffer: ArrayBufferView,
    /* optional */ dstOffset?: number | undefined,
    /* optional */ length?: number | undefined,
) {
    const sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0)!;
    gl.flush();

    await CPUWaitForGPUAsync(gl, sync, 0, 10);
    gl.deleteSync(sync);

    gl.bindBuffer(target, buffer);
    gl.getBufferSubData(target, srcByteOffset, dstBuffer, dstOffset, length);
    gl.bindBuffer(target, null);

    return dstBuffer;
}
export interface AsyncPixelReadingState {
    bReadingPixels: boolean;
}
export async function ReadPixelsAsync(
    gl: WebGL2RenderingContext,
    intermediateBuffer: WebGLBuffer,
    x: number,
    y: number,
    width: number,
    height: number,
    format: GLenum,
    type: GLenum,
    dest: ArrayBufferView,
    stateRef: AsyncPixelReadingState,
) {
    if (stateRef.bReadingPixels) {
        return;
    }

    stateRef.bReadingPixels = true;

    try {
        //Read pixels into intermediate GPU buffer
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, intermediateBuffer);
        gl.readPixels(x, y, width, height, format, type, 0);
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);

        await GetBufferSubDataAsync(gl, gl.PIXEL_PACK_BUFFER, intermediateBuffer, 0, dest);
    } finally {
        stateRef.bReadingPixels = false;
    }

    //return dest;
}
